import { createAsyncThunk, createSlice, isFulfilled, isRejected } from "@reduxjs/toolkit";
import { apiGet, apiPost, apiPut } from "../../lib/axiosInstance";
import { logger } from "../../lib/utils";


const initialState = {
	isAuthenticated: false,
	isLoading:false,
	user: null,
	token: null,
	error: null,
	message: null
}
const setPending = (state) => {
	state.isLoading = true;
	state.error = null;
	state.message = null;
}
const setRejected = (state, action) => {
	state.error = action.payload;
	state.message = null;
	state.isLoading = false;
}
const setFulfilled = (state, action) => {
	state.isLoading = false;
	state.error = null;
	state.message = action.payload?.message;
	state.isAuthenticated = true;
	state.user = action.payload.user;
	if(action.payload.token){
		state.token = action.payload.token;
		const remember = localStorage.getItem('remember-me') === 'true';
		if(remember){
			localStorage.setItem('token', action.payload.token);
		}else{
			sessionStorage.setItem('token', action.payload.token);
		}
	}
	
};

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		clearError: (state, action) => {
			state.error = null;
		},
		clearMessage: (state, action) => {
			state.message = null;
		},
		logOutUser:(state,action)=>{
			state.isAuthenticated = false;
			state.user = null;
			state.token = null;
			localStorage.removeItem('token')
			sessionStorage.removeItem('token')
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(registerUser.pending, setPending)
			.addCase(registerUser.rejected, setRejected)
			.addCase(registerUser.fulfilled, setFulfilled)
			.addCase(requestPasswordReset.pending, setPending)
			.addCase(requestPasswordReset.rejected, setRejected)
			.addCase(verifyPasswordResetOtp.pending, setPending)
			.addCase(verifyPasswordResetOtp.rejected, setRejected)
			.addCase(finalizePasswordReset.pending, setPending)
			.addCase(finalizePasswordReset.rejected, setRejected)
			.addCase(finalizePasswordReset.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.message = action.payload?.message;
			})
			.addCase(loginUser.pending, setPending)
			.addCase(loginUser.rejected, setRejected)
			.addCase(loginUser.fulfilled, setFulfilled)
			.addCase(getUser.pending, setPending)
			.addCase(getUser.rejected, (state,action) => {
				state.isAuthenticated = false
				state.user = null;
				state.token = null;
				state.error = action.payload?.message || null;
				state.message = null;
				state.isLoading = false;
				const parseRememberMe = localStorage.getItem('remember-me')
				if(parseRememberMe  === 'true'){
					localStorage.removeItem('token')
				}else{
					sessionStorage.removeItem('token')
				}
				logger.error('Get user rejected, removing token.',state.isLoading, action.payload);
			})
			.addCase(getUser.fulfilled, (state,action) => {
				state.isAuthenticated = true
				state.error = null;
				state.message = null;
				state.user = action.payload?.user ?? null;
				state.isLoading = false;
				logger.debug("Auth fulfilled, setting token.", action.payload);
			})
			.addCase(updateUser.pending, setPending)
			.addCase(updateUser.rejected, setRejected)
			.addCase(updateUser.fulfilled, setFulfilled)
			.addCase(updatePassword.pending, setPending)
			.addCase(updatePassword.rejected, setRejected)
			.addCase(updatePassword.fulfilled, (state,action) => {
				state.isLoading = false;
				state.error = null;
				state.message = action.payload?.message;
			})
			/* .addCase(logInWithTokenGithub.pending, setPending)
			.addCase(logInWithTokenGithub.rejected, setRejected)
			.addCase(logInWithTokenGithub.fulfilled, (state,action) => {
				state.isLoading = false;
				state.error = null;
				state.message = action.payload?.message;
				state.user = action.payload?.user;
				state.isAuthenticated = true;
			}) */
			.addCase(logInWithGoogle.pending, setPending)
			.addCase(logInWithGoogle.rejected, setRejected)
			.addCase(logInWithGoogle.fulfilled, setFulfilled)
			.addCase(refreshAccessToken.fulfilled, (state,action) => {
				state.isLoading = false;
				state.error = null;
				const token = action.payload?.token;
				if(token){
					state.token = token;
					localStorage.setItem('token', token);
					localStorage.setItem('google-token', token);
				}
			})
			.addCase(refreshAccessToken.rejected, (state,action) => {
				state.isLoading = false;
				state.error = action.payload?.message;
				state.isAuthenticated = false;
				state.user = null;
				state.token = null;
				localStorage.removeItem('token');
				sessionStorage.removeItem('token');
			})
			.addCase(logOutbackend.pending, setPending)
			.addCase(logOutbackend.fulfilled, (state,action) => {
				state.isAuthenticated = false;
				state.user = null;
				state.token = null;
				const parseRememberMe = localStorage.getItem('remember-me')
				if(parseRememberMe  === 'true'){
					localStorage.removeItem('token')
				}else{
					sessionStorage.removeItem('token')
				}
			})
			// Ensure isLoading is false for any fulfilled/rejected we might add later
			.addMatcher(isFulfilled, (state) => {
				state.isLoading = false;
			})
			.addMatcher(isRejected, (state) => {
				state.isLoading = false;
			});
	},
});

export const registerUser = createAsyncThunk(
	'/authentication/register', // Action type
	async (data, { rejectWithValue }) => {
		try {
			const response = await apiPost(`/auth/register`, data);
			return response; // On success, return the response data (user details, token, etc.)
		} catch (error) {
			// If an error occurs, reject the action with error response data
			return rejectWithValue(error.response ? error.response.data : error.message);
		}
	}
);
export const loginUser = createAsyncThunk('/authentication/login', async (data,{rejectWithValue}) => {
	try {
		const response = await apiPost(`/auth/login`, data);
		return response
	} catch (error) {
		logger.error("Login error:", error.response ? error.response.data : error.message);
		return rejectWithValue(error.response ? error.response.data : error.message);
	}
})

export const requestPasswordReset = createAsyncThunk('/authentication/forgot-password', async (email,{rejectWithValue}) => {
	try {
		const response = await apiPost(`/auth/forgot-password`, { email });
		return response;
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
});

export const verifyPasswordResetOtp = createAsyncThunk('/authentication/verify-reset-otp', async (data,{rejectWithValue}) => {
	try {
		const response = await apiPost(`/auth/verify-reset-otp`, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
});

export const finalizePasswordReset = createAsyncThunk('/authentication/reset-password', async (data,{rejectWithValue}) => {
	try {
		const response = await apiPost(`/auth/reset-password`, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
});


export const logInWithGoogle = createAsyncThunk('/authentication/google/login', async (token,{rejectWithValue}) => {
	try {
		localStorage.setItem('google-token', token);
		const response = await apiPost(`/auth/google?token=${token}`);
		return response
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
})
export const getUser = createAsyncThunk('/authentication/get-user', async (_,{rejectWithValue}) => {
	try {
		const response = await apiGet(`/auth/get-user`);
		return response
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
})

export const updateUser = createAsyncThunk('/authentication/update-user', async (data,{rejectWithValue}) => {
	try {
		const response = await apiPut(`/auth/update-user`,data);
		return response
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
})

export const updatePassword = createAsyncThunk('/authentication/update-password', async (data,{rejectWithValue}) => {
	try {
		const response = await apiPut(`/auth/update-password`,data);
		return response
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
})
export const logOutbackend = createAsyncThunk('/authentication/logout', async (_,{rejectWithValue}) => {
	try {
		const response = await apiPost(`/auth/logout`);
		return response
	} catch (error) {
		return rejectWithValue(error.response ? error.response.data : 'An error occurred');
	}
})
export const updateUserSettings = createAsyncThunk(
	'/authentication/update-user-settings',
	async (data, { rejectWithValue }) => {
		try {
			const response = await apiPut(`/auth/update-user-settings`, data);
			return response;
		} catch (error) {
			return rejectWithValue(error.response ? error.response.data : 'An error occurred');
		}
	});
export const refreshAccessToken = createAsyncThunk('/authentication/refresh', async (_,{rejectWithValue})=>{
    try{
        const response = await apiPost(`/auth/refresh`, {}, { withCredentials: true });
        return response;
    }catch(error){
        return rejectWithValue(error.response ? error.response.data : 'An error occurred');
    }
});


export const {clearError,clearMessage,logOutUser} = authSlice.actions
export default authSlice.reducer